import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { Users, UserCheck, CalendarDays, Award, Star, ArrowRight, Video, Mic, Smartphone, User, BrainCircuit, Grip, Calendar, Clock } from 'lucide-react';
import './StatsAndWorkshops.css';

const StatCounter = ({ value, suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration: 2.5,
        ease: "easeOut",
        onUpdate(v) {
          setCount(v);
        }
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {count.toFixed(decimals)}{suffix}
    </span>
  );
};
const StatsAndWorkshops = () => {
  let chairClapperSrc = '';
  try { chairClapperSrc = require('../../assets/img/chair-clapper.png'); } 
  catch(e) { chairClapperSrc = 'https://ui-avatars.com/api/?name=Workshop&background=random'; }

  return (
    <>
      <section className="stats-strip-wrapper">
        <div className="stats-inner-box">
          
          <div className="stat-item">
            <span className="stat-icon stat-purple">
              <Users size={34} strokeWidth={2} />
            </span>
            <div className="stat-content">
              <span className="stat-number"><StatCounter value={25} suffix="K+" /></span>
              <span className="stat-label">Talents Registered</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item">
            <span className="stat-icon stat-blue">
              <UserCheck size={34} strokeWidth={2} />
            </span>
            <div className="stat-content">
              <span className="stat-number"><StatCounter value={2.5} suffix="K+" decimals={1} /></span>
              <span className="stat-label">Verified Casting<br/>Directors</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item">
            <span className="stat-icon stat-red">
              <CalendarDays size={34} strokeWidth={2} />
            </span>
            <div className="stat-content">
              <span className="stat-number"><StatCounter value={15} suffix="K+" /></span>
              <span className="stat-label">Auditions<br/>Facilitated</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item">
            <span className="stat-icon stat-blue">
              <Award size={34} strokeWidth={2} />
            </span>
            <div className="stat-content">
              <span className="stat-number"><StatCounter value={98} suffix="%" /></span>
              <span className="stat-label">Profile Feedback<br/>From Users</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item google-review-stat">
            <div className="google-logo">
              {/* Google G Logo SVG */}
              <svg width="34" height="34" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.7 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-number review-score">
                4.9/5 
                <span className="stars">
                  <Star fill="#f59e0b" color="#f59e0b" size={16} />
                  <Star fill="#f59e0b" color="#f59e0b" size={16} />
                  <Star fill="#f59e0b" color="#f59e0b" size={16} />
                  <Star fill="#f59e0b" color="#f59e0b" size={16} />
                  <Star fill="#f59e0b" color="#f59e0b" size={16} />
                </span>
              </span>
              <span className="stat-label">Based on 2,000+ Reviews</span>
            </div>
          </div>

        </div>
      </section>

      <section className="workshops-wrapper">
        <div className="workshops-inner banner-style">
          <div className="workshops-left">
            <h2 className="section-title text-red">LEARN. PERFORM. GROW.</h2>
            <p className="workshops-desc">Join workshops & masterclasses by industry experts and<br/>take your skills to the next level.</p>
            
            {/* Decorative background dashed line element */}
            <div className="dashed-loop-deco">
              <svg width="100%" height="100" viewBox="0 0 200 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 50 Q 50 50 100 20 T 150 50 Q 180 80 160 90 T 100 50" stroke="#fcd34d" strokeWidth="2" strokeDasharray="5 5" fill="none" opacity="0.6"/>
              </svg>
            </div>

            <div className="workshop-icons-row">
              <div className="workshop-icon-item">
                <span className="wk-icon"><Video size={24} strokeWidth={1.5} /></span>
                <span>Acting for<br/>Camera</span>
              </div>
              <div className="workshop-icon-item">
                <span className="wk-icon"><Mic size={24} strokeWidth={1.5} /></span>
                <span>Voice &<br/>Dialogue</span>
              </div>
              <div className="workshop-icon-item">
                <span className="wk-icon"><Smartphone size={24} strokeWidth={1.5} /></span>
                <span>Self-Tape<br/>Mastery</span>
              </div>
              <div className="workshop-icon-item">
                <span className="wk-icon"><User size={24} strokeWidth={1.5} /></span>
                <span>Kids &<br/>Teens</span>
              </div>
              <div className="workshop-icon-item">
                <span className="wk-icon"><BrainCircuit size={24} strokeWidth={1.5} /></span>
                <span>Confidence<br/>Building</span>
              </div>
              <div className="workshop-icon-item">
                <span className="wk-icon"><Grip size={24} strokeWidth={1.5} /></span>
                <span>And<br/>More!</span>
              </div>
            </div>
          </div>

          <div className="workshops-right">
            <div className="upcoming-content">
              <span className="upcoming-badge">Upcoming Workshop</span>
              <h3 className="upcoming-title">Acting for Camera – Beginners</h3>
              <p className="upcoming-author">by Industry Coach</p>
              
              <div className="upcoming-details">
                <span className="detail-item"><Calendar size={14} className="text-red" /> 25 May 2024</span>
                <span className="detail-item"><Clock size={14} className="text-red" /> 4:00 PM (Online)</span>
              </div>
              
              <div className="upcoming-price">
                <span className="price">₹499</span>
                <span className="seats-left">• Limited Seats</span>
              </div>
              
              <button className="btn-solid red-btn">
                Explore Workshops <ArrowRight size={16} style={{marginLeft: '8px'}}/>
              </button>
            </div>
            
            <div className="upcoming-image-wrapper">
               <img src={chairClapperSrc} alt="Director Chair" className="upcoming-image" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default StatsAndWorkshops;
